import { useEffect, useState, useCallback } from 'react';
import { toast } from 'react-toastify';
import Layout from '../components/Layout';
import contactService from '../services/contactService';
import contentService from '../services/contentService';
import featureFlags from '../utils/featureFlags';
import useContentUpdates from '../hooks/useContentUpdates';

// Hardcoded content - preserved for fallback and feature flag toggle
const HARDCODED_CONTACT_CONTENT = {
  description: "Lorem ipsum is simply dummy text of the printing and typesetting industry. Lorem Ipsum has been the industry's standard.",
  locations: [
    {
      id: '1',
      title: 'United States',
      mobile: '+(44) 609 33 17 54',
      email: 'email@website.com',
      address: '201 Oak Street 27 Manchester, UK'
    },
    {
      id: '2',
      title: 'Australia',
      mobile: '+(44) 609 33 17 54',
      email: 'email@website.com',
      address: '201 Oak Street 27 Manchester, UK'
    }
  ]
};

function Contact() {
  // Form state
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    subject: '',
    message: ''
  });

  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Contact content state (CMS or hardcoded)
  const [contactContent, setContactContent] = useState(HARDCODED_CONTACT_CONTENT);

  // Handle input changes
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    // Clear error for this field
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  // Validate form
  const validateForm = () => {
    const newErrors = {};
    const name = `${formData.firstName} ${formData.lastName}`.trim();

    // Name validation (2-100 characters)
    if (name.length < 2) {
      newErrors.name = 'Name must be at least 2 characters';
    } else if (name.length > 100) {
      newErrors.name = 'Name must not exceed 100 characters';
    }

    // Email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!formData.email) {
      newErrors.email = 'Email is required';
    } else if (!emailRegex.test(formData.email)) {
      newErrors.email = 'Please enter a valid email address';
    }

    // Subject validation (5-200 characters)
    if (!formData.subject) {
      newErrors.subject = 'Subject is required';
    } else if (formData.subject.length < 5) {
      newErrors.subject = 'Subject must be at least 5 characters';
    } else if (formData.subject.length > 200) {
      newErrors.subject = 'Subject must not exceed 200 characters';
    }

    // Message validation (10-5000 characters)
    if (!formData.message) {
      newErrors.message = 'Message is required';
    } else if (formData.message.length < 10) {
      newErrors.message = 'Message must be at least 10 characters';
    } else if (formData.message.length > 5000) {
      newErrors.message = 'Message must not exceed 5000 characters';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      toast.error('Please fix the errors in the form');
      return;
    }

    setIsSubmitting(true);

    try {
      // Combine first and last name
      const name = `${formData.firstName} ${formData.lastName}`.trim();

      const contactData = {
        name,
        email: formData.email,
        subject: formData.subject,
        message: formData.message
      };

      const response = await contactService.submitContact(contactData);

      if (response.success) {
        // Use backend message (CMS-configurable)
        toast.success(response.message || 'Thank you! Your message has been sent successfully.');
        // Reset form
        setFormData({
          firstName: '',
          lastName: '',
          email: '',
          subject: '',
          message: ''
        });
      } else {
        toast.error(response.message || 'Failed to send message. Please try again.');
      }
    } catch (error) {
      console.error('Contact form error:', error);

      // Extract user-friendly message from backend response
      const errorMessage = error.response?.data?.message
        || error.message
        || 'Failed to send message. Please try again.';

      toast.error(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  // Fetch contact content from CMS - extracted to reusable function for WebSocket updates
  const fetchContactContent = useCallback(async () => {
    // Check feature flag - skip API call if disabled
    if (!featureFlags.useCmsContent()) {
      if (process.env.NODE_ENV === 'development') {
        console.log('[Contact] CMS content disabled, using hardcoded content');
      }
      return;
    }

    try {
      const result = await contentService.getContactPage();
      if (result.success && result.data?.parsedContent) {
        setContactContent(result.data.parsedContent);
        if (process.env.NODE_ENV === 'development') {
          console.log('[Contact] Loaded contact content from CMS');
        }
      } else {
        console.warn('[Contact] CMS fetch failed, using hardcoded content');
      }
    } catch (err) {
      console.error('[Contact] Error fetching contact content:', err);
      // Fallback to hardcoded content (already set as initial state)
    }
  }, []);

  // Initial fetch on mount
  useEffect(() => {
    fetchContactContent();
  }, [fetchContactContent]);

  // Listen for real-time content updates via WebSocket
  useContentUpdates({
    onPageUpdated: (slug, title) => {
      // Refresh contact page content when 'contact' slug is updated
      if (slug === 'contact') {
        if (process.env.NODE_ENV === 'development') {
          console.log(`[Contact] Real-time update received for contact page - ${title}`);
        }
        fetchContactContent(); // Refetch content from API
        toast.info('Contact page has been updated', { autoClose: 2000 });
      }
    }
  });

  useEffect(() => {
    // Handle window load event (fade out loader)
    const handleWindowLoad = () => {
      // Fade out loader (800ms fade as in original)
      if (window.$ && window.$('.loader1').length > 0) {
        window.$(".loader1").fadeOut(800);
        window.$('.side-menu').removeClass('hidden');
      }
    };

    // If window is already loaded, trigger immediately
    if (document.readyState === 'complete') {
      handleWindowLoad();
    } else {
      // Otherwise wait for window load event
      window.addEventListener('load', handleWindowLoad);
    }

    // Initialize WOW animations
    if (window.WOW) {
      new window.WOW().init();
    }

    // Cleanup
    return () => {
      window.removeEventListener('load', handleWindowLoad);
    };
  }, []);

  return (
    <Layout>
      {/* Contact Section */}
      <section className="contact-sec contact-sec-no-map" id="contact-sec">

        <div className="container">
          <div className="row">
            <div className="col-12 col-lg-6 contact-description text-center text-lg-left wow slideInLeft" data-wow-delay=".8s">
              <div className="contact-detail wow fadeInLeft">
                <div className="ex-detail">
                  <span className="fly-text">CONTACT US</span>
                  <h4 className="large-heading">
                    <span className="heading-1">Get</span>
                    <span className="heading-2">In Touch</span>
                  </h4>
                </div>
                <p className="small-text text-center text-md-left">
                  {contactContent.description}
                </p>
                <div className="row location-details text-center text-md-left">
                  {contactContent.locations.map((location, index) => (
                    <div
                      key={location.id || index}
                      className={`col-12 col-md-6 ${index === 0 ? 'country-1' : 'couuntry-1'}`}
                    >
                      <h4 className="heading-text text-left">{location.title}</h4>
                      <ul>
                        <li>
                          <i className="fas fa-mobile-alt"></i>
                          <a href={`tel:${location.mobile.replace(/[^+\d]/g, '')}`}>{location.mobile}</a>
                        </li>
                        <li>
                          <i className="fas fa-envelope"></i>
                          <a href={`mailto:${location.email}`}>{location.email}</a>
                        </li>
                        <li>
                          <i className="fas fa-map-marker"></i>
                          <a href="/" onClick={(e) => e.preventDefault()}>{location.address}</a>
                        </li>
                      </ul>
                    </div>
                  ))}
                </div>
              </div>
            </div>
            <div className="col-12 col-lg-6 contact-box text-center text-md-left wow slideInRight" data-wow-delay=".8s">
              <div className="c-box wow fadeInRight">
                <h4 className="small-heading">Leave Message</h4>
                <form className="contact-form" id="contact-form-data" onSubmit={handleSubmit}>
                  <div className="row my-form">
                    <div className="col-md-12 col-sm-12">
                      <div id="result">
                        {errors.name && <div className="text-danger small mb-2">{errors.name}</div>}
                      </div>
                    </div>
                    <div className="col-12 col-md-6">
                      <input
                        type="text"
                        className={`form-control ${errors.name ? 'is-invalid' : ''}`}
                        id="candidate_fname"
                        name="firstName"
                        placeholder="First Name"
                        value={formData.firstName}
                        onChange={handleInputChange}
                        required
                      />
                    </div>
                    <div className="col-12 col-md-6">
                      <input
                        type="text"
                        className={`form-control ${errors.name ? 'is-invalid' : ''}`}
                        id="candidate_lname"
                        name="lastName"
                        placeholder="Last Name"
                        value={formData.lastName}
                        onChange={handleInputChange}
                        required
                      />
                    </div>
                    <div className="col-12 col-md-6">
                      <input
                        type="email"
                        className={`form-control ${errors.email ? 'is-invalid' : ''}`}
                        id="user_email"
                        name="email"
                        placeholder="Email"
                        value={formData.email}
                        onChange={handleInputChange}
                        required
                      />
                      {errors.email && <div className="text-danger small mt-1">{errors.email}</div>}
                    </div>
                    <div className="col-12 col-md-6">
                      <input
                        type="text"
                        className={`form-control ${errors.subject ? 'is-invalid' : ''}`}
                        id="user_subject"
                        name="subject"
                        placeholder="Subject"
                        value={formData.subject}
                        onChange={handleInputChange}
                        required
                      />
                      {errors.subject && <div className="text-danger small mt-1">{errors.subject}</div>}
                    </div>
                    <div className="col-12">
                      <textarea
                        className={`form-control ${errors.message ? 'is-invalid' : ''}`}
                        id="user_message"
                        name="message"
                        placeholder="Message"
                        rows="7"
                        value={formData.message}
                        onChange={handleInputChange}
                        required
                      ></textarea>
                      {errors.message && <div className="text-danger small mt-1">{errors.message}</div>}
                    </div>
                    <div className="col-12">
                      <button
                        type="submit"
                        className="btn pink-gradient-btn-into-black user-contact contact_btn"
                        disabled={isSubmitting}
                      >
                        {isSubmitting ? 'SENDING...' : 'SUBMIT'}
                      </button>
                    </div>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>
      </section>
    </Layout>
  );
}

export default Contact;
